import React, { useRef } from 'react'
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react'
import { toast } from '../toast/CustomToast'

const HELP_TEXT =
  'Abra a versão mobile do Hub no seu smartphone, toque em "Escanear QR Code" e aponte a câmera para este código para fazer login automaticamente.'

// Payload contract expected by the frontend-company-mobile QR login flow
const buildQrPayload = ({ companyId, username, token }) =>
  JSON.stringify({ company_id: companyId, username, token })

const QR_EXPORT_SIZE = 216
const QR_EXPORT_PADDING = 32

const QRLoginModal = ({ isOpen, onClose, companyId, username, token }) => {
  const downloadCanvasRef = useRef(null)

  if (!isOpen) return null

  const hasToken = Boolean(token)
  const qrValue = hasToken ? buildQrPayload({ companyId, username, token }) : ''

  const handleDownload = () => {
    const canvas = downloadCanvasRef.current
    if (!canvas) return

    // Composite onto a larger white canvas so the exported file keeps a margin
    // around the QR Code, matching the framed look shown on screen.
    const framedCanvas = document.createElement('canvas')
    const framedSize = QR_EXPORT_SIZE + QR_EXPORT_PADDING * 2
    framedCanvas.width = framedSize
    framedCanvas.height = framedSize

    const ctx = framedCanvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, framedSize, framedSize)
    ctx.drawImage(canvas, QR_EXPORT_PADDING, QR_EXPORT_PADDING)

    framedCanvas.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'n4h-solidflow.png'
      a.click()
      URL.revokeObjectURL(url)
      toast.success('QR Code baixado!')
    }, 'image/png')
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-black/10 dark:border-white/10 p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            title="Fechar"
          >
            <span className="material-icons sm">close</span>
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="w-11 h-11 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-3">
              <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '22px' }}>
                qr_code_2
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Login pelo celular</h2>
              <span
                className="material-icons text-gray-400 dark:text-gray-500 cursor-help"
                style={{ fontSize: '16px' }}
                title={HELP_TEXT}
              >
                help_outline
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 max-w-[260px]">
              Acesse a versão mobile pelo smartphone e aponte a câmera para este QR Code ou salve-o para upload.
            </p>

            {hasToken ? (
              <>
                <div className="mt-5 p-4 bg-white rounded-2xl border-4 border-gray-100 dark:border-gray-700 shadow-inner">
                  <QRCodeSVG
                    value={qrValue}
                    size={216}
                    level="M"
                    bgColor="#ffffff"
                    fgColor="#0A1C28"
                  />
                </div>
                {/* Hidden canvas twin, used only to export the QR Code as a PNG file */}
                <QRCodeCanvas
                  ref={downloadCanvasRef}
                  value={qrValue}
                  size={QR_EXPORT_SIZE}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#0A1C28"
                  className="hidden"
                />

                <button
                  type="button"
                  onClick={handleDownload}
                  className="mt-4 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
                >
                  <span className="material-icons" style={{ fontSize: '14px' }}>download</span>
                  Baixar QR Code
                </button>

                <span className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-800/40">
                  <span className="material-icons flex-shrink-0" style={{ fontSize: '13px' }}>warning_amber</span>
                  <strong className="font-semibold">ATENÇÃO:</strong> Não compartilhe este QR Code com terceiros
                </span>
              </>
            ) : (
              <div className="mt-5 flex flex-col items-center gap-2 py-6 px-4 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30">
                <span className="material-icons text-amber-500 dark:text-amber-400" style={{ fontSize: '28px' }}>
                  warning_amber
                </span>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Nenhum token de API disponível para esta empresa. Gere um token para habilitar o login pelo celular.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default QRLoginModal
